/* Curated Brand-Kit font catalog.
 *
 * PRIVACY-CRITICAL: every webfont here is SELF-HOSTED (woff2 served same-origin
 * from /fonts via brand-fonts.css @font-face) — NOTHING is loaded from a Google
 * CDN (fonts.googleapis.com / fonts.gstatic.com). That would leak every user's
 * IP to Google (GDPR issue for a UK/EU product) and break the "licence-clean
 * self-hosted" intent. All webfonts below are OFL / open-licence, licence-clean
 * to self-host. `stack` always ends in a system fallback so the picker + render
 * degrade gracefully until a given woff2 is populated (see app/public/fonts).
 *
 * Keep the `family` values in lockstep with the server allowlist
 * (BrandKitService::SAFE_FONTS) — a font outside it is rejected on save.
 */

export type FontCategory = 'System' | 'Sans' | 'Serif' | 'Display';

export interface BrandFont {
  /** exact family name — must match SAFE_FONTS + the @font-face family */
  family: string;
  category: FontCategory;
  /** CSS font-family stack used for preview + render (self-hosted, sys fallback) */
  stack: string;
  /** true = OS/system font, no webfont fetch at all */
  system?: boolean;
}

const SANS = "system-ui, -apple-system, 'Segoe UI', sans-serif";
const SERIF = "Georgia, 'Times New Roman', serif";

export const BRAND_FONTS: BrandFont[] = [
  // --- System (no webfont) ---
  { family: 'system-ui', category: 'System', stack: SANS, system: true },
  { family: 'Georgia', category: 'System', stack: `Georgia, ${SERIF}`, system: true },
  { family: 'Arial', category: 'System', stack: `Arial, ${SANS}`, system: true },

  // --- Self-hosted OFL sans ---
  { family: 'Inter', category: 'Sans', stack: `'Inter', ${SANS}` },
  { family: 'Roboto', category: 'Sans', stack: `'Roboto', ${SANS}` },
  { family: 'Open Sans', category: 'Sans', stack: `'Open Sans', ${SANS}` },
  { family: 'Lato', category: 'Sans', stack: `'Lato', ${SANS}` },
  { family: 'Montserrat', category: 'Sans', stack: `'Montserrat', ${SANS}` },
  { family: 'Poppins', category: 'Sans', stack: `'Poppins', ${SANS}` },
  { family: 'Raleway', category: 'Sans', stack: `'Raleway', ${SANS}` },
  { family: 'Nunito', category: 'Sans', stack: `'Nunito', ${SANS}` },
  { family: 'Work Sans', category: 'Sans', stack: `'Work Sans', ${SANS}` },
  { family: 'DM Sans', category: 'Sans', stack: `'DM Sans', ${SANS}` },
  { family: 'Source Sans 3', category: 'Sans', stack: `'Source Sans 3', ${SANS}` },
  { family: 'Space Grotesk', category: 'Sans', stack: `'Space Grotesk', ${SANS}` },

  // --- Self-hosted OFL serif / display ---
  { family: 'Source Serif 4', category: 'Serif', stack: `'Source Serif 4', ${SERIF}` },
  { family: 'Merriweather', category: 'Serif', stack: `'Merriweather', ${SERIF}` },
  { family: 'Lora', category: 'Serif', stack: `'Lora', ${SERIF}` },
  { family: 'Playfair Display', category: 'Display', stack: `'Playfair Display', ${SERIF}` },
  { family: 'Oswald', category: 'Display', stack: `'Oswald', ${SANS}` },
  { family: 'Bebas Neue', category: 'Display', stack: `'Bebas Neue', ${SANS}` },
];

export const BRAND_FONT_FAMILIES = BRAND_FONTS.map((f) => f.family);

export function fontStack(family: string): string {
  return BRAND_FONTS.find((f) => f.family === family)?.stack ?? SANS;
}
